import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';

import { globalConfig } from './configuration/config';
import { authStore } from './core/stores/auth-store';
import { openModal } from './core/modal/open-modal';
import { InsufficientResourceDialog } from './components/dialogs/insufficient-resource-dialog';

type TokenGetter = () => string | undefined;

export class Api {
  private axios: AxiosInstance;
  private getToken: TokenGetter | null = null;
  private getIdToken: TokenGetter | null = null;

  init() {
    this.axios = axios.create({
      baseURL: globalConfig.config.baseApiUrl,
    });

    this.axios.interceptors.request.use(async (config) => {
      const token = this.getToken?.();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }

      // The API needs the id_token only to link/provision on first login.
      // Forward it on /me exclusively.
      if (config.url === '/me' || config.url === 'me') {
        const idToken = this.getIdToken?.();
        if (idToken) {
          config.headers['X-Id-Token'] = idToken;
        }
      }

      return config;
    });

    this.axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error?.response?.status === 507) {
          openModal(InsufficientResourceDialog, { reason: 'storage-limit' });
        }
        if (error?.response?.status === 401) {
          authStore.setAuthorized(false);
        }
        return Promise.reject(error);
      },
    );
  }

  setTokenGetter(getter: TokenGetter) {
    this.getToken = getter;
  }

  setIdTokenGetter(getter: TokenGetter) {
    this.getIdToken = getter;
  }

  getAccessToken(): string | undefined {
    return this.getToken?.();
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () =>
      this.axios.get(url, {
        ...config,
      });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) {
          return request();
        }
      });
    }
  }

  async post<T>(
    url: string,
    data?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T> | any> {
    const request = () =>
      this.axios.post(url, data, {
        ...config,
      });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) {
          return request();
        }
      });
    }
  }

  updateSpaceInHeaders(spaceId: string) {
    this.axios.defaults.headers['X-Space-Id'] = spaceId;
  }

  getSpaceIdFromHeaders(): string | undefined {
    return this.axios.defaults.headers['X-Space-Id'] as string | undefined;
  }

  updateTokenInHeaders(token: string) {
    this.axios.defaults.headers['Authorization'] = `Bearer ${token}`;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () =>
      this.axios.delete(url, {
        ...config,
      });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) {
          return request();
        }
      });
    }
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () =>
      this.axios.put(url, data, {
        ...config,
      });

    try {
      return await request();
    } catch (e) {
      return await this.handleError(e as AxiosError).then((res) => {
        if (res.retry) {
          return request();
        }
      });
    }
  }

  async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T | any> {
    const request = () =>
      this.axios.patch(url, data, {
        ...config,
      });
    return await request();
  }

  private async handleError(error: AxiosError): Promise<any> {
    if (!error.response) {
      throw error;
    }

    throw error;
  }
}

const api = new Api();

export { api };
